import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, RouteObject } from 'react-router-dom';

import { ADMIN_ROLES } from '@/shared/config/access-roles';
import { APP_ROUTES } from '@/shared/config/routes-config';
import { RouteConfig } from '@/shared/interfaces/routes-config.interface';
import DefaultLayout from '@/shared/layouts/default/default.layout';

const LeaveRequestsListContainer = React.lazy(
  () => import('../../containers/leave-requests-list/leave-requests-list.container')
);

const LeaveDashboardContainer = React.lazy(
  () => import('../../containers/leave-dashboard/leave-dashboard.container')
);

const TeamLeaveRequestsContainer = React.lazy(
  () => import('../../containers/team-leave-requests/team-leave-requests.container')
);

const TeamLeaveDashboardContainer = React.lazy(
  () => import('../../containers/team-leave-dashboard/team-leave-dashboard.container')
);

/**
 * The HR / Leave module is visible to every internal admin role — every
 * employee should be able to file and track their own leave. Horilla
 * enforces "own data only" on the downstream API as a second layer of
 * defense.
 *
 * This used to be a hand-written copy of that list and had fallen behind the
 * enum; it now reads the shared {@link ADMIN_ROLES}.
 */
const HR_PERMITTED_ROLES = ADMIN_ROLES;

const HrRouteFallback: FC = () => {
  const { t } = useTranslation();
  return <div>{t('HR_MODULE.LOADING')}</div>;
};

const createProtectedRoute = (component: FC, addLayout = false): React.ReactElement => (
  <React.Suspense fallback={<HrRouteFallback />}>
    <DefaultLayout
      {...(addLayout && { addLayout })}
      authenticationRequired={true}
      permittedList={HR_PERMITTED_ROLES}
      component={component}
    />
  </React.Suspense>
);

const ROUTES = APP_ROUTES.HR_MODULE.CHILDREN as RouteConfig;

export const HrRoutesConfig: RouteObject[] = [
  {
    index: true,
    element: <Navigate to={ROUTES.LEAVE_REQUESTS_LIST.FULL_PATH} />
  },
  {
    path: ROUTES.LEAVE_REQUESTS_LIST.FULL_PATH,
    element: createProtectedRoute(LeaveRequestsListContainer)
  },
  {
    path: ROUTES.LEAVE_REQUEST_DETAILS.FULL_PATH,
    element: createProtectedRoute(LeaveRequestsListContainer)
  },
  {
    path: ROUTES.LEAVE_DASHBOARD.FULL_PATH,
    element: createProtectedRoute(LeaveDashboardContainer)
  },
  {
    path: ROUTES.TEAM_LEAVE_REQUESTS.FULL_PATH,
    element: createProtectedRoute(TeamLeaveRequestsContainer)
  },
  {
    path: ROUTES.TEAM_LEAVE_DASHBOARD.FULL_PATH,
    element: createProtectedRoute(TeamLeaveDashboardContainer)
  }
];
